import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { GithubActionsApi } from '@backstage-community/plugin-github-actions';
import { Octokit } from '@octokit/rest';

/**
 * Implementação de `GithubActionsApi` que fala com a API do GitHub através
 * do proxy do backend (`/github/api`, configurado em `app-config.yaml`) em
 * vez de exigir sign-in com GitHub via `githubAuthApiRef` — que é o
 * comportamento padrão de `GithubActionsClient`.
 *
 * Myralis usa Microsoft Entra/guest como login do portal (ver `App.tsx`), não
 * GitHub; o proxy injeta a autenticação usando o `GITHUB_TOKEN` de
 * integração já existente em `integrations.github`. Ver F-0029 design.md D2.
 *
 * Só suporta `github.com` (único host configurado em `integrations.github`
 * hoje). Se um host GitHub Enterprise for adicionado no futuro, este cliente
 * precisa ganhar um proxy/endpoint por host.
 */
export class ProxiedGithubActionsClient implements GithubActionsApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async getOctokit(hostname = 'github.com') {
    if (hostname !== 'github.com') {
      throw new Error(
        `ProxiedGithubActionsClient só suporta github.com; host recebido: ${hostname}`,
      );
    }
    const proxyUrl = await this.options.discoveryApi.getBaseUrl('proxy');
    return new Octokit({
      baseUrl: `${proxyUrl}/github/api`,
      request: { fetch: this.options.fetchApi.fetch },
    });
  }

  async reRunWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<any> {
    const { hostname, owner, repo, runId } = options;
    const octokit = await this.getOctokit(hostname);
    return octokit.actions.reRunWorkflow({ owner, repo, run_id: runId });
  }

  async listWorkflowRuns(options: {
    hostname?: string;
    owner: string;
    repo: string;
    pageSize?: number;
    page?: number;
    branch?: string;
  }) {
    const { hostname, owner, repo, pageSize = 100, page = 0, branch } = options;
    const octokit = await this.getOctokit(hostname);
    const workflowRuns = await octokit.actions.listWorkflowRunsForRepo({
      owner,
      repo,
      per_page: pageSize,
      page,
      ...(branch ? { branch } : {}),
    });
    return workflowRuns.data;
  }

  async getWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }) {
    const { hostname, owner, repo, id } = options;
    const octokit = await this.getOctokit(hostname);
    const workflow = await octokit.actions.getWorkflow({
      owner,
      repo,
      workflow_id: id,
    });
    return workflow.data;
  }

  async getWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }) {
    const { hostname, owner, repo, id } = options;
    const octokit = await this.getOctokit(hostname);
    const run = await octokit.actions.getWorkflowRun({
      owner,
      repo,
      run_id: id,
    });
    return run.data;
  }

  async listJobsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
    pageSize?: number;
    page?: number;
  }) {
    const { hostname, owner, repo, id, pageSize = 100, page = 0 } = options;
    const octokit = await this.getOctokit(hostname);
    const jobs = await octokit.actions.listJobsForWorkflowRun({
      owner,
      repo,
      run_id: id,
      per_page: pageSize,
      page,
    });
    return jobs.data;
  }

  async downloadJobLogsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }) {
    const { hostname, owner, repo, runId } = options;
    const octokit = await this.getOctokit(hostname);
    const workflow = await octokit.actions.downloadJobLogsForWorkflowRun({
      owner,
      repo,
      job_id: runId,
    });
    return workflow.data;
  }

  async listBranches(options: {
    hostname?: string;
    owner: string;
    repo: string;
    page: number;
  }) {
    const { hostname, owner, repo, page } = options;
    const octokit = await this.getOctokit(hostname);
    const response = await octokit.rest.repos.listBranches({
      owner,
      repo,
      per_page: 100,
      page,
    });
    return response.data;
  }

  async getDefaultBranch(options: {
    hostname?: string;
    owner: string;
    repo: string;
  }) {
    const { hostname, owner, repo } = options;
    const octokit = await this.getOctokit(hostname);
    const response = await octokit.rest.repos.get({ owner, repo });
    return response.data.default_branch;
  }
}
